import React, { createContext, useContext, ReactNode, useEffect } from "react";
import { useNotifications } from "@/hooks/useNotifications";
import type {
  Notification,
  PageMetadata,
  NotificationSearchParams,
  NotificationDetail,
} from "../../types/notification";

interface NotificationContextType {
  notifications: Notification[];
  loading: boolean;
  metadata: PageMetadata | null;
  unreadCount: number;
  isConnected: boolean;
  searchNotifications: (params: NotificationSearchParams) => Promise<number>;
  markAsRead: (ids: number[]) => Promise<void>;
  getNotificationDetail: (id: number) => Promise<NotificationDetail | null>;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const notificationState = useNotifications();

  // Listen for global refresh events so other parts of the app can trigger
  // a refresh of the unread count without needing access to hooks.
  useEffect(() => {
    const handler = async () => {
      try {
        const prev = notificationState.unreadCount;
        // call searchNotifications and get the returned unread count
        const returnedUnread = await notificationState.searchNotifications({
          page: 0,
          size: 1,
          sort: "id desc",
          isRead: false,
        });

        const curr =
          typeof returnedUnread === "number" ? returnedUnread : notificationState.unreadCount;

        if (curr !== prev) {
          try {
            // debug log
            // eslint-disable-next-line no-console
            console.info("Dispatching notification:refreshed", { prev, current: curr });
            window.dispatchEvent(
              new CustomEvent("notification:refreshed", {
                detail: { prev, current: curr },
              })
            );
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error("Failed to dispatch notification:refreshed", err);
          }
        }
      } catch (err) {
        // swallow errors
        // eslint-disable-next-line no-console
        console.error("Failed to refresh notifications from global event", err);
      }
    };

    window.addEventListener("notification:refresh", handler);
    return () => {
      window.removeEventListener("notification:refresh", handler);
    };
  }, [notificationState]);

  return (
    <NotificationContext.Provider value={notificationState}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotificationContext must be used within a NotificationProvider");
  }
  return context;
};
